/**
 * Development tools for OptoScale
 * Provides feature flag management utilities for development environment
 */

import { getEnvironmentByHostname, isNonProduction } from './environmentByHostname';

declare global {
    interface Window {
        __OPTOSCALE_DEV__: {
            featureFlags: {
                enable: (flagName: string) => void;
                disable: (flagName: string) => void;
                toggle: (flagName: string) => void;
                remove: (flagName: string) => void;
                clear: () => void;
                listConfigured: () => Record<string, boolean>;
                listAvailable: () => readonly string[];
            };
        };
        FF: Window['__OPTOSCALE_DEV__']['featureFlags'];
    }
}

/**
 * Initialize the development tools API
 * This function sets up the feature flags management interface on the window object
 * Only available in non-production environments
 */
export const initializeDevTools = (): void => {
    if (isNonProduction()) {
        // Import the feature flags utilities dynamically to avoid bundling in production
        const loaded = Promise.all([
            import('./localFeatureFlags'),
            import('../config/availableFeatureFlags'),
        ]).then(([localFeatureFlagsModule, availableFeatureFlagsModule]) => {
            const {
                setLocalFeatureFlag,
                removeLocalFeatureFlag,
                clearLocalFeatureFlags,
                listLocalFeatureFlags,
                getLocalFeatureFlags,
            } = localFeatureFlagsModule;
            const { AVAILABLE_FEATURE_FLAGS } = availableFeatureFlagsModule;
            // Internal API
            window.__OPTOSCALE_DEV__ = {
                featureFlags: {
                    enable: (flagName: string) => {
                        setLocalFeatureFlag(flagName, true);
                        console.log(`Feature flag "${flagName}" enabled`);
                    },
                    disable: (flagName: string) => {
                        setLocalFeatureFlag(flagName, false);
                        console.log(`Feature flag "${flagName}" disabled`);
                    },
                    toggle: (flagName: string) => {
                        const flags = getLocalFeatureFlags();
                        const currentValue = flags[flagName] || false;
                        setLocalFeatureFlag(flagName, !currentValue);
                        console.log(`Feature flag "${flagName}" toggled to ${!currentValue}`);
                    },
                    remove: (flagName: string) => {
                        removeLocalFeatureFlag(flagName);
                        console.log(`Feature flag "${flagName}" removed`);
                    },
                    clear: () => {
                        clearLocalFeatureFlags();
                        console.log('All feature flags cleared');
                    },
                    listConfigured: () => {
                        const flags = listLocalFeatureFlags();
                        const activeFlags = Object.entries(flags).filter(([, enabled]) => enabled);
                        console.log(
                            'Configured feature flags (from session storage):',
                            activeFlags.length > 0 ? activeFlags.map(([name]) => name) : 'None'
                        );
                        return flags;
                    },
                    listAvailable: () => {
                        console.log(
                            'Available feature flags in the application:',
                            AVAILABLE_FEATURE_FLAGS
                        );
                        return AVAILABLE_FEATURE_FLAGS;
                    },
                },
            };

            // Friendly API shortcut
            window.FF = window.__OPTOSCALE_DEV__.featureFlags;

            // Log the active feature flags
            const currentEnvironment = getEnvironmentByHostname();
            const activeFlags = listLocalFeatureFlags();
            const activeFlagsList = Object.entries(activeFlags).map(
                ([name, enabled]) => `${name}: ${enabled ? 'enabled' : 'disabled'}`
            );

            console.log(
                `OptoScale Dev Tools loaded! Use FF.enable("FLAG_NAME") to activate feature flags on ${currentEnvironment.toUpperCase()} environment.`
            );
            console.log(
                'Available commands: FF.enable(), FF.disable(), FF.toggle(), FF.remove(), FF.clear(), FF.listConfigured(), FF.listAvailable()'
            );
            if (activeFlagsList.length > 0) {
                console.log(`Configured feature flags: ${activeFlagsList}`);
            }
        });
        loaded.catch((error: unknown) => {
            // Developer tooling only: say why the console API is missing instead of rejecting.
            console.warn('[devTools] The feature flag tools could not load', error);
        });
    }
};
